import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { correlationId } from '@interlucent/ids';
import { useFocusTrap } from '../utils/focusTrap';
import { send } from '../ws';
import { waitForAck } from '../utils/waitForAck';
import { CopyButton } from './shared';
import { CommandArgsBuilder } from './CommandArgsBuilder';
import { EnvVarsEditor } from './EnvVarsEditor';
import { isArgDisallowed, isEnvVarDisallowed } from './versionFieldDisallowList';
import { usePlatformAdmin } from '../hooks/usePlatformAdmin';
import type { AppVersion } from '@interlucent/organization-protocol';
import { logger } from '../log';
import styles from './EditVersionDialog.module.css';

const log = logger('edit-version');

interface EditVersionDialogProps {
  isOpen: boolean;
  version: AppVersion;
  onClose: () => void;
  onSaved?: () => void;
}

type DialogState = 'viewing' | 'saving' | 'error';

export const EditVersionDialog: React.FC<EditVersionDialogProps> = ({
  isOpen,
  version,
  onClose,
  onSaved,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { isPlatformAdmin } = usePlatformAdmin();
  const [state, setState] = useState<DialogState>('viewing');
  const [error, setError] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);

  // Local version fields
  const [description, setDescription] = useState<string>('');

  // Regular version fields (executablePath is read-only)
  const [cmdArgs, setCmdArgs] = useState<string>('');
  const [environmentVariables, setEnvironmentVariables] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  // Pixel Streaming protocol generation: '' = auto, '1' = legacy, '2' = PS2.
  const [streamingProtocolVersion, setStreamingProtocolVersion] = useState<'' | '1' | '2'>('');
  // Per-version worker↔game rendezvous timeout override (seconds).
  // Empty string = no override (orchestrator uses its default, currently 90s).
  const [workerGameRendezvousSeconds, setWorkerGameRendezvousSeconds] = useState<string>('');

  const isLocal = version.isLocal;

  const engineOS = useMemo(() => ({
    gameEngine: version.gameEngine,
    operatingSystem: version.operatingSystem,
  }), [version.gameEngine, version.operatingSystem]);

  const checkArgDisallowed = useCallback(
    (arg: string) => isArgDisallowed(arg, engineOS),
    [engineOS],
  );

  const checkEnvDisallowed = useCallback(
    (key: string) => isEnvVarDisallowed(key, engineOS),
    [engineOS],
  );

  // Enable focus trap
  useFocusTrap(modalRef, isOpen);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setState('viewing');
      setError('');
      setHasChanges(false);

      // Local version fields
      setDescription(version.description || '');

      // Regular version fields (executablePath is read-only, not editable)
      setCmdArgs(version.cmdArgs || '');
      setEnvironmentVariables(version.environmentVariables || '');
      setNotes(version.notes || '');
      const spv = version.streamingProtocolVersion;
      setStreamingProtocolVersion(spv === 1 ? '1' : spv === 2 ? '2' : '');
      const wgrs = version.workerGameRendezvousSeconds;
      setWorkerGameRendezvousSeconds(
        typeof wgrs === 'number' && wgrs > 0 ? String(wgrs) : '',
      );
    }
  }, [isOpen, version]);

  // Track changes
  useEffect(() => {
    if (isLocal) {
      setHasChanges(description !== (version.description || ''));
    } else {
      const argsChanged = cmdArgs !== (version.cmdArgs || '');
      const envChanged = environmentVariables !== (version.environmentVariables || '');
      const notesChanged = notes !== (version.notes || '');
      const spvCurrent = version.streamingProtocolVersion === 1
        ? '1'
        : version.streamingProtocolVersion === 2
          ? '2'
          : '';
      const spvChanged = streamingProtocolVersion !== spvCurrent;
      const wgrsCurrent =
        typeof version.workerGameRendezvousSeconds === 'number' &&
        version.workerGameRendezvousSeconds > 0
          ? String(version.workerGameRendezvousSeconds)
          : '';
      const wgrsChanged = workerGameRendezvousSeconds !== wgrsCurrent;
      setHasChanges(argsChanged || envChanged || notesChanged || spvChanged || wgrsChanged);
    }
  }, [description, cmdArgs, environmentVariables, notes, streamingProtocolVersion, workerGameRendezvousSeconds, version, isLocal]);

  // Handle keyboard events
  useEffect(() => {
    if (isOpen) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  const handleSave = useCallback(async () => {
    setState('saving');
    setError('');

    try {
      const corr = correlationId();

      if (isLocal) {
        // Update local version description
        send('localVersion.update', {
          id: version.id,
          description: description || null,
        }, corr);
      } else {
        // Update regular version config (executablePath is immutable)
        const wgrsTrimmed = workerGameRendezvousSeconds.trim();
        const wgrsParsed = wgrsTrimmed === '' ? null : Number(wgrsTrimmed);
        const wgrsToSend =
          wgrsParsed !== null && Number.isFinite(wgrsParsed) && wgrsParsed >= 1 && wgrsParsed <= 600
            ? Math.trunc(wgrsParsed)
            : null;
        send('version.update', {
          id: version.id,
          cmdArgs: cmdArgs || null,
          environmentVariables: environmentVariables || null,
          notes: notes || null,
          streamingProtocolVersion:
            streamingProtocolVersion === '1' ? 1 :
            streamingProtocolVersion === '2' ? 2 : null,
          workerGameRendezvousSeconds: wgrsToSend,
        }, corr);
      }

      // Use AbortController for proper cleanup on timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      try {
        await waitForAck(corr, { signal: controller.signal });
      } catch (err: any) {
        // Convert abort error to user-friendly message
        if (err?.name === 'AbortError') {
          throw new Error('Request timed out');
        }
        throw err;
      } finally {
        clearTimeout(timeoutId);
      }

      setState('viewing');
      setHasChanges(false);
      onSaved?.();
      onClose();
    } catch (err: any) {
      log.error('failed to save version', err);
      setError(err.message || 'Failed to save changes');
      setState('error');
    }
  }, [version.id, description, cmdArgs, environmentVariables, notes, streamingProtocolVersion, workerGameRendezvousSeconds, isLocal, onSaved, onClose]);

  if (!isOpen) return null;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div
      className={styles.modalOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-dialog-title"
    >
      <div ref={modalRef} className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 id="edit-dialog-title" className={styles.modalTitle}>
          Version Details
        </h2>

        {/* Version ID */}
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Version ID</span>
          <div className={styles.detailValue}>
            <span className={styles.monoValue}>{version.id}</span>
            <CopyButton value={version.id} aria-label="Copy version ID" />
          </div>
        </div>

        {/* Type indicator */}
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Type</span>
          <span className={`${styles.typeBadge} ${isLocal ? styles.local : styles.uploaded}`}>
            {isLocal ? 'Local' : 'Uploaded'}
          </span>
        </div>

        {/* Created Date */}
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Created</span>
          <span className={styles.detailValue}>{formatDate(version.createdAt)}</span>
        </div>

        {/* Checksum (for uploaded versions) */}
        {!isLocal && version.checksumSha256 && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Checksum</span>
            <div className={styles.detailValue}>
              <span className={styles.checksumValue}>
                {version.checksumSha256.slice(0, 16)}...
              </span>
              <CopyButton value={version.checksumSha256} aria-label="Copy checksum" />
            </div>
          </div>
        )}

        {/* Game Engine */}
        {version.gameEngine && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Engine</span>
            <span className={styles.detailValue}>
              {version.gameEngine === 'unreal' ? 'Unreal Engine' : 'Unity'}
            </span>
          </div>
        )}

        {/* OS */}
        {version.operatingSystem && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Platform</span>
            <span className={styles.detailValue}>
              {version.operatingSystem === 'windows' ? 'Windows' : 'Linux'}
            </span>
          </div>
        )}

        <div className={styles.divider} />

        {/* Editable fields */}
        {isLocal ? (
          // Local version: only description is editable
          <div className={styles.formGroup}>
            <label htmlFor="description-input" className={styles.label}>
              Description
            </label>
            <textarea
              id="description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description for this local version..."
              className={styles.textarea}
              rows={3}
            />
            <span className={styles.hint}>
              Describe what this local development version is for
            </span>
          </div>
        ) : (
          // Regular version: args, env vars, notes are editable; executablePath is immutable
          <>
            {/* Executable Path (read-only) */}
            {version.executablePath && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Executable Path</span>
                <div className={styles.detailValue}>
                  <span className={styles.monoValue}>{version.executablePath}</span>
                  <CopyButton value={version.executablePath} aria-label="Copy executable path" />
                </div>
              </div>
            )}

            <div className={styles.divider} />

            <div className={styles.formGroup}>
              <label htmlFor="cmd-args-input" className={styles.label}>
                Command Arguments
              </label>
              <CommandArgsBuilder
                id="cmd-args-input"
                value={cmdArgs}
                onChange={setCmdArgs}
                checkDisallowed={checkArgDisallowed}
              />
              <span className={styles.hint}>
                Command line arguments to pass when launching
              </span>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="env-vars-input" className={styles.label}>
                Environment Variables
              </label>
              <EnvVarsEditor
                id="env-vars-input"
                value={environmentVariables}
                onChange={setEnvironmentVariables}
                checkDisallowed={checkEnvDisallowed}
              />
              <span className={styles.hint}>
                Environment variables (KEY=value format)
              </span>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="notes-input" className={styles.label}>
                Notes
              </label>
              <textarea
                id="notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this version..."
                className={styles.textarea}
                rows={2}
              />
              <span className={styles.hint}>
                Internal notes visible only to your team
              </span>
            </div>

            {isPlatformAdmin && version.gameEngine === 'unreal' && (
              <div className={styles.formGroup}>
                <label htmlFor="streaming-protocol-version-input" className={styles.label}>
                  Pixel Streaming Version
                </label>
                <select
                  id="streaming-protocol-version-input"
                  value={streamingProtocolVersion}
                  onChange={(e) =>
                    setStreamingProtocolVersion(e.target.value as '' | '1' | '2')
                  }
                  className={styles.input}
                >
                  <option value="">Auto (infer from engine version)</option>
                  <option value="1">Pixel Streaming 1.x (UE 4.27 – 5.4)</option>
                  <option value="2">Pixel Streaming 2 (UE 5.5+)</option>
                </select>
                <span className={styles.hint}>
                  Select PS2 for builds packaged with the new Pixel Streaming 2 plugin
                  (UE 5.5+). Auto picks PS2 for engine version &gt;= 5.5, otherwise legacy.
                </span>
              </div>
            )}

            {isPlatformAdmin && (
              <div className={styles.formGroup}>
                <label htmlFor="worker-game-rendezvous-input" className={styles.label}>
                  Worker / Game Rendezvous (seconds)
                </label>
                <input
                  id="worker-game-rendezvous-input"
                  type="number"
                  min={1}
                  max={600}
                  step={1}
                  value={workerGameRendezvousSeconds}
                  onChange={(e) => setWorkerGameRendezvousSeconds(e.target.value)}
                  placeholder="Default (90s)"
                  className={styles.input}
                />
                <span className={styles.hint}>
                  How long the orchestrator waits for the launched game to connect back as a
                  streamer. Leave blank to use the default (90s). Increase for heavy VR / large
                  packaged builds whose first-launch shader compile exceeds the default. Range:
                  1–600s.
                </span>
              </div>
            )}
          </>
        )}

        {state === 'error' && (
          <div className={styles.errorState}>
            <span className={styles.errorIcon}>!</span>
            <span className={styles.errorMessage}>{error}</span>
          </div>
        )}

        <div className={styles.buttonGroup}>
          <button
            onClick={onClose}
            className={`${styles.button} ${styles.buttonSecondary}`}
          >
            {hasChanges ? 'Cancel' : 'Close'}
          </button>
          {hasChanges && (
            <button
              onClick={handleSave}
              disabled={state === 'saving'}
              className={`${styles.button} ${styles.buttonPrimary}`}
            >
              {state === 'saving' ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
